'use client';

import MinusCircleSvg from '@/assets/svgs/jobs/minus-circle.svg';
import PlusIconSvg from '@/assets/svgs/plus.svg';
import { inter500, inter600 } from '@/config/fonts';
import IconButton from '@/shared/components/Buttons/IconButton/IconButton';
import classNames from 'classnames';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';

export interface AssignmentListProps<T> {
  assignedItems: T[];
  availableItems: T[];
  LineItemView: React.ComponentType<{
    data: T;
    actions: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
  }>;
  onAssign: (item: T) => void;
  onUnassign: (item: T) => void;
  onAssignAll?: () => void;
  isLoading?: boolean;
  searchText?: string;
  emptyMessage?: React.ReactNode;
  getItemKey: (item: T) => string | number;
  assignButtonLabel?: string;
  unassignButtonLabel?: string;
  canUnassignItem?: (item: T) => boolean;
  getUnassignTooltip?: (item: T) => string;
}

// Threshold for enabling virtualization (items count)
const VIRTUALIZATION_THRESHOLD = 250;

export function AssignmentList<T>({
  assignedItems,
  availableItems,
  LineItemView,
  onAssign,
  onUnassign,
  onAssignAll,
  isLoading = false,
  searchText = '',
  emptyMessage = 'No items found',
  getItemKey,
  assignButtonLabel = 'Assign',
  unassignButtonLabel = 'Remove',
  canUnassignItem,
  getUnassignTooltip,
}: AssignmentListProps<T>) {
  const shouldUseVirtualization = availableItems.length >= VIRTUALIZATION_THRESHOLD;

  return (
    <>
      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center">
          <span className="loading loading-spinner"></span>
        </div>
      )}

      {/* Assigned Items */}
      {!isLoading && !!assignedItems?.length && (
        <div className="assigned-container mb-4">
          <div
            className={classNames(
              'section-header dark:bg-base-300 flex h-[36px] items-center justify-between rounded-t-lg rounded-b-[4px] bg-gray-100 px-3 text-[13px]',
              inter500.className
            )}>
            <div>
              Assigned{' '}
              <span className="text-gray-default dark:text-gray-400">
                ({assignedItems.length})
              </span>
            </div>
          </div>

          {assignedItems.map((item, idx) => {
            const canUnassign = canUnassignItem ? canUnassignItem(item) : true;
            const tooltip = getUnassignTooltip
              ? getUnassignTooltip(item)
              : unassignButtonLabel;

            return (
              <LineItemView
                data={item}
                className="relative h-16"
                key={String(getItemKey(item))}
                style={{
                  zIndex: assignedItems.length - idx,
                }}
                actions={
                  <IconButton
                    className="btn-outline border-blue-200 shadow-sm"
                    tooltip={tooltip}
                    tooltipPosition="left"
                    icon={<MinusCircleSvg />}
                    onClick={() => onUnassign(item)}
                    disabled={!canUnassign}
                  />
                }
              />
            );
          })}
        </div>
      )}

      {/* Available Items */}
      {!isLoading && (
        <div className="available-container flex flex-col gap-1">
          <div
            className={classNames(
              'section-header dark:bg-base-300 flex h-[36px] items-center justify-between rounded-t-lg rounded-b-[4px] bg-gray-100 px-3 text-[13px]',
              inter500.className
            )}>
            <div className="flex items-center gap-1">
              {searchText ? `Results for '${searchText}'` : 'Available'}{' '}
              <span className="text-gray-default dark:text-gray-400">
                ({availableItems.length})
              </span>
            </div>
            {onAssignAll && !!availableItems.length && (
              <button
                onClick={onAssignAll}
                className={classNames(
                  'btn btn-ghost btn-xs text-primary h-7 gap-1',
                  inter600.className
                )}>
                <PlusIconSvg className="h-4 w-4" /> Assign All
              </button>
            )}
          </div>

          {!availableItems?.length && (
            <div className="mt-3 flex flex-col items-center justify-center gap-3 rounded-md text-center text-sm">
              {emptyMessage}
            </div>
          )}

          {!!availableItems.length && (
            <div className="flex flex-col gap-2">
              {shouldUseVirtualization ? (
                <Virtuoso
                  style={{ height: '100%', width: '100%' }}
                  data={availableItems}
                  itemContent={(index, item) => {
                    return (
                      <LineItemView
                        data={item}
                        className="h-16"
                        style={{ marginBottom: '8px' }}
                        actions={
                          <button
                            onClick={() => onAssign(item)}
                            className={classNames(
                              'btn btn-ghost text-primary gap-1 text-sm',
                              inter600.className
                            )}>
                            <PlusIconSvg className="h-5 w-5" /> {assignButtonLabel}
                          </button>
                        }
                      />
                    );
                  }}
                  computeItemKey={(index, item) => String(getItemKey(item))}
                />
              ) : (
                availableItems.map((item) => {
                  return (
                    <LineItemView
                      data={item}
                      className="h-16"
                      key={String(getItemKey(item))}
                      actions={
                        <button
                          onClick={() => onAssign(item)}
                          className={classNames(
                            'btn btn-ghost text-primary gap-1 text-sm',
                            inter600.className
                          )}>
                          <PlusIconSvg className="h-5 w-5" /> {assignButtonLabel}
                        </button>
                      }
                    />
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
